import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { useNoIndex } from '@/hooks/useNoIndex';
import { I18N_KEYS } from '@/language/keys';
import { Box } from '@/UI/Box/Box';
import { H1, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Input } from '@/UI/Input/Input';
import { posthog } from '@/providers/posthog';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import type { User } from '@/types/auth.types';

type ClaimResult = { alreadyRedeemed: boolean };

export function WorkshopPage({ user, isSigningIn }: { user: User | null; isSigningIn: boolean }) {
  useNoIndex(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<ClaimResult | null>(null);
  const [error, setError] = useState('');

  async function claim(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError('');
    try {
      const response = await safeAxios.post<ClaimResult>(API_ROUTES.workshops.claim(), {
        code,
      });
      setCode('');
      setResult(response.data);
      posthog.reloadFeatureFlags();
    } catch (cause) {
      const status = isAxiosError(cause) ? cause.response?.status : undefined;
      setError(
        status === 403
          ? I18N_KEYS.WORKSHOP_PAGE.INVALID_CODE
          : status === 401
            ? I18N_KEYS.WORKSHOP_PAGE.SESSION_EXPIRED
            : I18N_KEYS.WORKSHOP_PAGE.CLAIM_FAILED,
      );
    } finally {
      setPending(false);
    }
  }

  if (isSigningIn) return <PageLoader />;

  return (
    <Box sx={{ maxWidth: 440, mx: 'auto', px: 3, py: 8 }}>
      <H1>{t(I18N_KEYS.WORKSHOP_PAGE.TITLE)}</H1>
      {!user ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
          <TextStdRg>{t(I18N_KEYS.WORKSHOP_PAGE.SIGN_IN_INTRO)}</TextStdRg>
          <ButtonContained onClick={() => navigate('/signin?redirect=%2Fworkshops')}>
            {t(I18N_KEYS.WORKSHOP_PAGE.SIGN_IN)}
          </ButtonContained>
        </Box>
      ) : result ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
          <Box role="status" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextStdRg>{t(I18N_KEYS.WORKSHOP_PAGE.SUCCESS)}</TextStdRg>
            {result.alreadyRedeemed && <TextStdRg>{t(I18N_KEYS.WORKSHOP_PAGE.ALREADY_GRANTED)}</TextStdRg>}
          </Box>
          <ButtonContained onClick={() => navigate('/')}>{t(I18N_KEYS.WORKSHOP_PAGE.START_CREATING)}</ButtonContained>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={(event) => {
            void claim(event);
          }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}
        >
          <TextStdRg>{t(I18N_KEYS.WORKSHOP_PAGE.INTRO)}</TextStdRg>
          <TextStdRg>{t(I18N_KEYS.WORKSHOP_PAGE.JOINING_AS, { email: user.email })}</TextStdRg>
          <Input
            id="workshop-code"
            label={t(I18N_KEYS.WORKSHOP_PAGE.CODE_LABEL)}
            type="text"
            autoComplete="off"
            required
            value={code}
            disabled={pending}
            inputProps={{ maxLength: 80 }}
            onChange={(event) => setCode(event.target.value)}
          />
          {error && <TextStdRg role="alert">{t(error)}</TextStdRg>}
          <ButtonContained type="submit" disabled={pending}>
            {t(pending ? I18N_KEYS.WORKSHOP_PAGE.PENDING : I18N_KEYS.WORKSHOP_PAGE.SUBMIT)}
          </ButtonContained>
        </Box>
      )}
    </Box>
  );
}
